import { LocalDate } from '@js-joda/core';
import { useCallback } from 'react';
import styled from 'styled-components';

import { Country } from '@dreryk/address-common-api';
import { IdentityDocument } from '@dreryk/egabinet-patient-published-language-api';

import { FormContextValues } from '@dreryk/react-common';
import { appendTestableElementAttributes } from '@dreryk/react-components';

import { IdentityDocumentIssuingCountryField } from '../../../common/IdentityDocument/IdentityDocumentIssuingCountryField';
import { IdentityDocumentSeriesAndNumberField } from '../../../common/IdentityDocument/IdentityDocumentSeriesAndNumberField';
import { IdentityDocumentValidToField } from '../../../common/IdentityDocument/IdentityDocumentValidToField';

import {
   ENTITLEMENT_STATEMENT_IDENTITY_DOCUMENT_SELECT_TYPES,
   EntitlementStatementIdentityDocumentSelectType,
   EntitlementStatementIdentityDocumentTypeField,
   getEntitlementStatementIdentityDocumentSelectType,
   getEntitlementStatementIdentityDocumentType,
   OnEntitlementStatementIdentityDocumentTypeSelected,
} from './EntitlementStatementIdentityDocumentTypeField';

export type EntitlementStatementIdentityDocumentFormPart = {
   type: EntitlementStatementIdentityDocumentSelectType;
   seriesAndNumber?: string | null;
   issuingCountry?: Country | null;
   validTo?: LocalDate | null;
};

export const createEntitlementStatementIdentityDocumentFormPart = (
   identityDocument: IdentityDocument | undefined
): EntitlementStatementIdentityDocumentFormPart => {
   if (
      !identityDocument ||
      !ENTITLEMENT_STATEMENT_IDENTITY_DOCUMENT_SELECT_TYPES.includes(
         getEntitlementStatementIdentityDocumentSelectType(identityDocument.getType())!
      )
   ) {
      return {
         type: 'IdentityCard',
      };
   }

   return {
      type: getEntitlementStatementIdentityDocumentSelectType(identityDocument.getType())!,
      seriesAndNumber: identityDocument.getSeriesAndNumber(),
      issuingCountry: identityDocument.getIssuingCountry(),
      validTo: identityDocument.getValidTo(),
   };
};

export const createIdentityDocumentFromFormPart = (
   entitlementStatementIdentityDocumentFormPart: EntitlementStatementIdentityDocumentFormPart
): IdentityDocument => {
   return IdentityDocument.of(
      getEntitlementStatementIdentityDocumentType(entitlementStatementIdentityDocumentFormPart.type)!,
      entitlementStatementIdentityDocumentFormPart.seriesAndNumber!,
      entitlementStatementIdentityDocumentFormPart.issuingCountry!,
      entitlementStatementIdentityDocumentFormPart.validTo!
   );
};

const StyledEntitlementStatementIdentityDocumentFormContentSection = styled.div`
   display: grid;
   grid-template-columns: 368px 346px;
   gap: 24px 36px;
`;
StyledEntitlementStatementIdentityDocumentFormContentSection.displayName =
   'StyledEntitlementStatementIdentityDocumentFormContentSection';

type EntitlementStatementIdentityDocumentFormSectionProps = Pick<FormContextValues, 'register' | 'setValue'> & {
   readonly name: string;
};

export const EntitlementStatementIdentityDocumentFormSection = (
   props: EntitlementStatementIdentityDocumentFormSectionProps
): JSX.Element => {
   const { name, setValue, register } = props;

   const handleEntitlementStatementIdentityDocumentTypeSelected = useCallback<OnEntitlementStatementIdentityDocumentTypeSelected>(() => {
      setValue(`${name}.seriesAndNumber`, null);
      setValue(`${name}.issuingCountry`, null);
      setValue(`${name}.validTo`, null);
   }, [name, setValue]);

   return (
      <div {...appendTestableElementAttributes(`${name}.identityDocumentFormSection`)}>
         <StyledEntitlementStatementIdentityDocumentFormContentSection>
            <EntitlementStatementIdentityDocumentTypeField
               name={`${name}.type`}
               testId={`${name}.type`}
               register={register}
               onSelect={handleEntitlementStatementIdentityDocumentTypeSelected}
            />
            <IdentityDocumentSeriesAndNumberField
               name={`${name}.seriesAndNumber`}
               testId={`${name}.seriesAndNumber`}
               register={register}
            />
            <IdentityDocumentIssuingCountryField
               name={`${name}.issuingCountry`}
               testId={`${name}.issuingCountry`}
               register={register}
            />
            <IdentityDocumentValidToField name={`${name}.validTo`} testId={`${name}.validTo`} register={register} />
         </StyledEntitlementStatementIdentityDocumentFormContentSection>
      </div>
   );
};
